import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { AgentCardDto, AgentType, SearchAgentCardDataDto } from '@workspace/data-types';
import { ApiService } from '@workspace/ng-http';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { AgentCardValidators } from '../../../utils/agent-card.validators';
import { VirtualCardData } from '../display/agent-card-display.component';

@Component({
  selector: 'etxe-agent-card-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './agent-card-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentCardFormComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  dto = input<AgentCardDto | null>(null);
  agentType = input.required<AgentType>();

  cardFound = output<SearchAgentCardDataDto>();
  numberControl = new FormControl('', [
    Validators.required,
    AgentCardValidators.format(() => this.expectedPrefix()),
  ]);
  verifiedData = signal<SearchAgentCardDataDto | null>(null);
  isLoading = signal(false);

  expectedPrefix = computed(() => (this.agentType() === 'titulaire' ? 'CPI' : 'ADC'));

  virtualCardData = computed<VirtualCardData | null>(() => {
    const data = this.verifiedData();
    if (!data) return null;
    return {
      ...data,
      type: this.agentType(),
    };
  });

  cardLabel = computed(() => {
    return this.agentType() === 'titulaire'
      ? 'Numéro de carte professionnelle (CPI)'
      : "Numéro d'attestation collaborateur (ADC)";
  });

  cardPlaceholder = computed(() => {
    return this.agentType() === 'titulaire' ? 'Ex: CPI97122024000000001' : 'Ex: ADC97122024000000084';
  });

  constructor() {
    effect(() => {
      const prefix = this.expectedPrefix();
      const current = (this.numberControl.value ?? '').trim().toUpperCase();
      if (!current || current === 'CPI' || current === 'ADC') {
        this.numberControl.setValue(prefix);
      }
      this.numberControl.updateValueAndValidity();
    });
  }

  onPaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData('text') ?? '';
    if (text) {
      event.preventDefault();
      const normalized = this.normalizeCardNumber(text);
      this.numberControl.setValue(normalized);
    }
  }

  normalizeCardNumber(raw: string | null): string {
    if (!raw) return '';
    let clean = raw.toUpperCase().replace(/\s+/g, '');
    const expected = this.expectedPrefix();

    // Strip duplicate occurrences of any known prefixes (e.g. ADCADC... or CPICPI...)
    const prefixes = ['CPI', 'ADC', 'RPA', 'LPS'];
    for (const p of prefixes) {
      while (clean.startsWith(p + p)) {
        clean = clean.substring(p.length);
      }
    }

    // Strip combination prefixes (e.g. CPIADC or ADCCPI)
    for (const p1 of prefixes) {
      for (const p2 of prefixes) {
        if (clean.startsWith(p1 + p2)) {
          clean = p2 + clean.substring((p1 + p2).length);
        }
      }
    }

    // If the user pasted/entered only digits (>= 3 digits), auto-prepend expected prefix
    if (/^\d{3,}$/.test(clean)) {
      clean = expected + clean;
    }

    // Limit to max 20 chars
    if (clean.length > 20) {
      clean = clean.substring(0, 20);
    }

    return clean;
  }

  ngOnInit() {
    const dto = this.dto();
    if (dto) {
      this.numberControl.setValue(dto.number, { emitEvent: false });
      this.verifiedData.set({
        number: dto.number,
        companyName: dto.companyName,
        siren: dto.siret,
        expiresAt: dto.expiresAt,
        cci: dto.cci ?? { id: '', name: 'Inconnu' },
      } as SearchAgentCardDataDto);
    }

    this.numberControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((val) => this.normalizeCardNumber(val)),
        tap((normalized) => {
          if (this.numberControl.value !== normalized) {
            this.numberControl.setValue(normalized, { emitEvent: false });
          }
        }),
        filter((number): number is string => !!number && this.numberControl.valid),
        debounceTime(500),
        distinctUntilChanged(),
        tap(() => this.isLoading.set(true)),
        switchMap((number) =>
          this.api
            .business()
            .etxe()
            .agentCards()
            .verify(number)
            .pipe(
              finalize(() => this.isLoading.set(false)),
              catchError((err) => {
                if (err.status === 404) {
                  this.numberControl.setErrors({ cardNotFound: true });
                }
                return of(null);
              }),
            ),
        ),
        tap((data) => {
          this.verifiedData.set(data);
          if (data) {
            this.cardFound.emit(data);
          }
        }),
      )
      .subscribe();
  }
}
